import { FunctionComponent } from 'react';
import GenericIcons from '../../../components/generics/GenericIcons';
import { useNavigate } from 'react-router-dom';
import '../../../css/PublicWhatWeDoPage.css';

const Component03: FunctionComponent = () => {
  const navigate = useNavigate();
  const goToLoginPage = () => navigate('/public/login');

  return (
    <div className='public-what-we-do-page-component-three-container vdr-wwd-doc'>
      <div className='vdr-section-head vdr-wwd-section-head'>
        <h2 className='public-what-we-do-page-title'>Gestión de datos de redes</h2>
        <small>Datos y modelos</small>
      </div>
      <p className='public-what-we-do-page-text'>
        La herramienta de Gestión de datos de redes permite cargar y consolidar los datos de
        infraestructura de las redes de distribución eléctrica en una base de datos centralizada.
        Con funciones avanzadas de corrección automática de errores, garantiza la integridad y
        optimiza la calidad de los datos. Además, la herramienta genera reportes detallados sobre la
        completitud y calidad de los datos, facilitando el monitoreo constante. Para mayor
        precisión, ofrece una interfaz intuitiva que permite corregir manualmente aquellos campos
        que no hayan sido corregidos de manera automática, asegurando una gestión de datos eficiente
        y confiable. Por último, la herramienta es capaz de generar modelos eléctricos detallados
        totalmente compatibles con el software de simulación eléctrica OpenDSS, lo que habilita la
        ejecución de variados estudios eléctricos automatizados mediante otras herramientas de la
        plataforma.
      </p>
      <figure style={{ margin: 0 }} className='public-what-we-do-page-component-img-container'>
        <GenericIcons
          icon='networkDataManagementDiagram'
          imageStyle={{
            width: '80%',
            height: 'auto'
          }}
        />
        <figcaption className='public-what-we-do-page-component-img-caption'>
          Figura 2: Herramienta de gestión de datos, un paso a paso del flujo de los datos.
        </figcaption>
      </figure>
      <p className='public-what-we-do-page-text'>
        Esta herramienta fue diseñada para que las empresas eléctricas carguen inicialmente los
        datos de infraestructura de sus redes en la base de datos implementada en la plataforma. A
        partir de ahí, se ejecutan automáticamente dos procesos: un diagnóstico de completitud de
        los datos cargados y la corrección automática de algunos errores detectados. Como resultado
        de ambos procesos, se genera un informe que se envía a la empresa eléctrica, en el cual se
        especifican los datos corregidos de manera automática y los que aún necesitan ser corregidos
        por la empresa para poder repetir el proceso.
      </p>
      <p className='public-what-we-do-page-text'>
        El usuario de la empresa eléctrica puede corregir o añadir los datos solicitados a través de
        una interfaz intuitiva dentro de la misma plataforma, lo que agiliza el proceso. Después de
        realizar las correcciones y repetir el diagnóstico de completitud, la herramienta genera
        automáticamente los modelos eléctricos de las redes en formato OpenDSS, los cuales
        posteriormente son validados mediante la comparación de datos de operación históricos con
        los resultados de simulaciones que replican dichas condiciones.
      </p>
      <p className='public-what-we-do-page-text'>
        Tras la fase de validación, si el modelo eléctrico es clasificado como "no válido", se envía
        un nuevo reporte a la empresa eléctrica, detallando las razones de la invalidación y
        posibles soluciones. En cambio, si el modelo es considerado "válido", ya estará disponible
        para ejecutar estudios eléctricos detallados con una precisión garantizada.
      </p>
      {/* Párrafo sobre la base de datos relacional (FALTANTE en el PDF) */}
      <p className='public-what-we-do-page-text'>
        {/* 
        
        */}
      </p>
      <p className='public-what-we-do-page-text'>
        La calidad de los datos es esencial, especialmente cuando se utilizan para desarrollar
        modelos eléctricos. Cualquier defecto en los datos, como información incompleta o
        incorrecta, puede generar errores en los resultados de los modelos, lo que comprometería la
        fiabilidad de las simulaciones. Este tipo de errores tiene un impacto directo en la toma de
        decisiones, ya que las conclusiones derivadas de resultados defectuosos pueden ser
        incorrectas y, en escenarios extremos, generar fallos en la planificación y operación de las
        redes. Por ello, garantizar la integridad de los datos no solo es una cuestión de precisión
        técnica, sino de asegurar que los resultados obtenidos sean confiables y útiles para la
        correcta planificación y operación de dichos sistemas. Algunos de los errores que suelen
        ocurrir al cargar las bases de datos son:
      </p>
      <ul className='public-what-we-do-page-list'>
        <li>
          <b>Error de integridad:</b> Cuando se cargan datos que no cumplen relaciones.
          <ul>
            <li>Se carga un alimentador y le asigno un ID de empresa que no existe.</li>
            <li>Se carga una línea sin su ID de catálogo de conductor.</li>
            <li>Se carga dos nodos con el mismo ID.</li>
          </ul>
        </li>
        <li>
          <b>Error de valor nulo:</b> Cuando no se le asigna un valor a un atributo que tiene la
          restricción notnull.
          <ul>
            <li>La posición de un transformador no puede ser nulo.</li>
            <li>La capacidad de una línea o transformador no puede ser nulo.</li>
          </ul>
        </li>
        <li>
          <b>Error valor fuera de rango:</b> Cuando se le asigna un valor a un atributo que está
          fuera de rango.
          <ul>
            <li>
              La capacidad de un transformador MT-BT debe estar dentro de un rango de valores.
            </li>
            <li>
              También pueden existir rangos dinámicos: la tensión del lado BT debe ser menor al MT.
            </li>
          </ul>
        </li>
      </ul>
      <p className='public-what-we-do-page-text'>
        Mediante algoritmos avanzados implementados en esta herramienta, es posible completar
        automáticamente datos faltantes, como la impedancia de las líneas y el número de fases,
        interpolando la información de tramos adyacentes. También se pueden resolver problemas de
        conectividad en el modelo eléctrico, como islas generadas por la falta de información de un
        tramo de línea o un nodo, a través de un algoritmo que añade líneas para conectar nodos,
        utilizando criterios de cercanía. Sin embargo, es fundamental señalar que, si bien estos
        procesos ayudan a mejorar la integridad de los datos, es preferible contar con bases de
        datos lo más completas y precisas posible, ya que ciertos errores no pueden ser solucionados
        mediante suposiciones o aproximaciones.
      </p>
      <p className='public-what-we-do-page-text'>
        Por ello, la herramienta genera un reporte detallado sobre la calidad y completitud de los
        datos, el cual puede ser revisado por la empresa eléctrica. Este reporte facilita la
        identificación de las tablas y campos que requieren corrección, permitiendo que las
        correcciones necesarias se realicen directamente en la plataforma. De este modo, se asegura
        la calidad de los datos para la correcta modelación, simulación y como consecuencia,
        resultados que alimentan la toma de decisiones de nuestros usuarios.
      </p>
      {/* Detalle del contenido del reporte (FALTANTE en el PDF) */}
      <p className='public-what-we-do-page-text'>
        {/* Aquí debes detallar el contenido del reporte */}
        El reporte generado por la herramienta incluye información detallada sobre la calidad y
        completitud de los datos cargados. Se listan los errores encontrados, clasificados por tipo
        (integridad, valor nulo, fuera de rango), y se indica la ubicación de cada error en las
        tablas de la base de datos. Además, se proporciona un resumen de las correcciones
        automáticas realizadas y se destacan los campos que requieren revisión manual por parte del
        usuario. El reporte también puede incluir estadísticas sobre la completitud de los datos,
        como el porcentaje de campos con valores válidos y la cantidad de errores por tabla.
      </p>
      <p className='public-what-we-do-page-text'>
        Ya verificada la completitud de los datos del alimentador considerando los criterios
        mencionados anteriormente, se realiza automáticamente la <b>modelación de la red</b>.
      </p>
      <p className='public-what-we-do-page-text'>
        Los modelos de redes eléctricas juegan un papel esencial en el diseño, análisis y
        optimización de las redes de distribución, permitiendo simular su operación bajo múltiples
        escenarios y generar análisis detallados en base a resultados. Los beneficios de contar con
        modelos de redes incluyen una mejor toma de decisiones, mayor eficiencia operativa,
        reducción de costos, identificación de cuellos de botella y una planificación más precisa.
        Dada la masiva integración de generación distribuida, panorama que solo se prevé que se
        intensifique y con ello prolifere la generación distribuida a escala residencial, la
        necesidad de modelos de redes MTBT detallados ha incrementado y continuará haciéndolo para
        hacer frente a los problemas con los que se enfrenta la planificación y operación de las
        redes.
      </p>
      <p className='public-what-we-do-page-text'>
        Modelamos las redes de distribución para que sean completamente compatibles con el software
        OpenDSS (Open Distribution System Simulator), desarrollado por EPRI (Electric Power Research
        Institute). Este software especializado permite realizar simulaciones detalladas de sistemas
        eléctricos de distribución, incluyendo flujos de potencia en redes desbalanceadas,
        simulaciones cuasi-estacionarias con series de tiempo, estudios de fallas, flickers y
        armónicos. OpenDSS también puede ser utilizado como una librería en lenguajes de
        programación como Matlab, Python y Julia, lo que expande sus capacidades y facilita su
        integración con diversas herramientas.
      </p>
      <p className='public-what-we-do-page-text'>
        Los modelos de redes que generamos abarcan tanto la media como la baja tensión y son
        trifásicos, con la capacidad de simular flujos desbalanceados. Consideramos componentes
        clave como líneas, transformadores, equipos de generación (PMGD y EG), almacenamiento, así
        como equipos de protección y operación, lo que permite una representación precisa y
        funcional de la infraestructura eléctrica.
      </p>
      <p className='public-what-we-do-page-text'>
        La validación de un modelo eléctrico cuasi-estático es un proceso crucial para asegurar que
        la representación matemática del sistema refleje de manera exacta su comportamiento en la
        realidad. Este proceso garantiza que las simulaciones sean fiables y útiles para la toma de
        decisiones y la planificación efectiva de las redes de distribución.
      </p>
      <p className='public-what-we-do-page-text'>
        Para validar los modelos, empleamos metodologías que permiten comparar los resultados de
        simulaciones de las redes con datos medidos históricos y rangos aceptables para algunas
        variables eléctricas como la tensión, corriente y el nivel de pérdidas totales de la red.
      </p>
      <p className='public-what-we-do-page-text'>
        Los enfoques de validación utilizados se dividen en dos tipos:
      </p>
      <p className='public-what-we-do-page-text'>
        <b>Comparación con Datos Reales Medidos:</b> Se obtienen datos de operación históricos del
        alimentador, como valores de voltaje, corriente y potencia en diferentes nodos de la red.
        Posteriormente, se simulan las mismas condiciones de operación en el modelo cuasi estático
        OpenDSS y se comparan los resultados del modelo con los datos históricos. Si las diferencias
        entre ambos son pequeñas y se encuentran dentro de un rango aceptable, se considera que el
        modelo es válido.
      </p>
      <p className='public-what-we-do-page-text'>
        Los criterios que utilizan este enfoque de validación son:
      </p>
      <ul className='public-what-we-do-page-list'>
        <li>Medidas en la cabecera (P,Q,V)</li>
        <li>Medidas en transformadores MT-BT (P,Q,V)</li>
        <li>Nivel de pérdidas</li>
      </ul>
      <p className='public-what-we-do-page-text'>
        Para los dos primeros criterios, la diferencia entre los datos históricos y los resultados
        del modelo no debe superar el 5% (RMSE). En cuanto al nivel de pérdidas del alimentador, la
        diferencia no debe exceder el 2%. Si estos umbrales se superan, el modelo se considera
        inválido debido a su insuficiente precisión en relación con los datos históricos.
      </p>
      <figure style={{ margin: 0 }} className='public-what-we-do-page-component-img-container'>
        <GenericIcons
          icon='powerProfileComparison'
          imageStyle={{
            width: '60%',
            height: 'auto'
          }}
        />
        <figcaption className='public-what-we-do-page-component-img-caption'>
          Figura 3: Comparación de perfil de potencia en la cabecera real medido con el perfil
          generado mediante las simulaciones de validación.
        </figcaption>
      </figure>
      <p className='public-what-we-do-page-text'>
        <b>Validación Basada en Criterio Experto:</b> En ausencia de datos de operación históricos
        del alimentador, se recurre a la validación cualitativa a través del juicio de expertos. Los
        expertos evalúan si el modelo produce resultados coherentes con el conocimiento que tienen
        sobre el funcionamiento de la red y su comportamiento esperado bajo ciertas condiciones de
        operación. Los criterios que hacen uso de este enfoque de validación son:
      </p>
      <ul className='public-what-we-do-page-list'>
        <li>Verificación de islas o mallas</li>
        <li>Verificación del nivel de carga de líneas y transformadores</li>
        <li>Verificación de tensión en nodos</li>
        <li>Verificación de desbalance</li>
      </ul>
      <p className='public-what-we-do-page-text'>
        Para los tres últimos criterios se determina si el resultado promedio es <b>aceptable</b>,{' '}
        <b>no aceptable</b>, o si <b>requiere revisión por parte de un experto</b>. La evaluación se
        realiza teniendo en cuenta los rangos definidos en la siguiente tabla:
      </p>
      <div className='vdr-wwd-table-wrap'>
        <table className='validation-expert-criteria-table'>
          <thead>
            <tr>
              <th>Variable</th>
              <th>Unidad</th>
              <th style={{ color: '#228B22' }}>Aceptable</th>
              <th style={{ color: '#DAA520' }}>Revisar</th>
              <th style={{ color: '#DC143C' }}>No aceptable</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Porcentaje de carga en línea</td>
              <td>%</td>
              <td>&lt;110</td>
              <td>[110,120]</td>
              <td>&gt;120</td>
            </tr>
            <tr>
              <td>Porcentaje de carga en transformador</td>
              <td>%</td>
              <td>&lt;130</td>
              <td>[130,140]</td>
              <td>&gt;140</td>
            </tr>
            <tr>
              <td>Tensión en cada punto de demanda</td>
              <td>p.u</td>
              <td>[0.9,1.1]</td>
              <td>[0.85,1.15]</td>
              <td>En otro caso</td>
            </tr>
            <tr>
              <td>Desequilibrio de tensión</td>
              <td>%</td>
              <td>&lt;2o3</td>
              <td>&lt;3o4</td>
              <td>&gt;3o4</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className='public-what-we-do-page-text'>
        En el caso de que alguno de los criterios mencionados sea considerado <b>no aceptable</b>,
        el modelo se clasifica inmediatamente como de <b>baja confiabilidad</b>. En consecuencia, no
        podrá utilizarse para ejecutar estudios eléctricos detallados hasta que se resuelva el
        problema identificado, lo cual requiere una revisión más específica y exhaustiva del caso.
        Esto garantiza mantener un estándar de precisión en todos los modelos que se dispongan a
        través de la plataforma, lo que a su vez asegura que los usuarios apoyen su toma de
        decisiones en resultados confiables.
      </p>
      <figure style={{ margin: 0 }} className='public-what-we-do-page-component-img-container'>
        <GenericIcons
          icon='VoltageProfilePerConsumer'
          imageStyle={{
            width: '60%',
            height: 'auto'
          }}
        />
        <figcaption className='public-what-we-do-page-component-img-caption'>
          Figura 4: Perfil de tensión de cada uno de los consumidores del alimentador bajo
          simulaciones de validación.
        </figcaption>
      </figure>
      <p className='public-what-we-do-page-text'>
        Por otro lado, utilizando las herramientas de VisiónDERRED en conjunto con OpenDSS se lleva
        a cabo una verificación exhaustiva de la existencia de islas y mallas dentro del modelo. En
        caso de identificar una isla, se procede a su resolución, mientras que, si se detecta una
        malla, se informa inmediatamente a la empresa de distribución sobre su existencia.
      </p>
      <div className='public-what-we-do-page-btn-container'>
        <button className='primary-button-style public-what-we-do-page-btn' onClick={goToLoginPage}>
          Explorar herramienta
          <span aria-hidden='true'>→</span>
        </button>
      </div>
    </div>
  );
};

export default Component03;
